import { useState } from 'react';

import type { CommentMessage, CommentThread, WorkspaceMember } from '../api';
import { t } from '../i18n';
import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { Glyph } from './icons';

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0] ?? '?';
  if (words.length <= 1) return first.slice(0, 2).toUpperCase();
  const last = words[words.length - 1] ?? first;
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

/** "14:32" today, "12 Oct" before that — the prototype's compact times. */
export function formatCommentTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** Render `@Name` runs in mint, like the prototype's mention segments. */
export function renderMessageText(text: string, members: WorkspaceMember[]): React.ReactNode {
  const names = members.map((member) => member.name).filter(Boolean);
  if (names.length === 0) return text;
  const pattern = new RegExp(`@(${names.map((name) => escapeRegExp(name)).join('|')})`, 'g');
  const parts = text.split(pattern);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="cmt-mention">
        @{part}
      </span>
    ) : (
      part
    ),
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The member whose name is being typed after the last `@`, if any. */
export function mentionQuery(draft: string): string | null {
  const match = /(^|\s)@([^\s@]*)$/.exec(draft);
  return match ? (match[2] ?? '') : null;
}

export function MentionList({
  draft,
  members,
  onPick,
}: {
  draft: string;
  members: WorkspaceMember[];
  onPick: (draft: string) => void;
}): React.ReactElement | null {
  const query = mentionQuery(draft);
  if (query === null) return null;
  const needle = query.toLowerCase();
  const items = members.filter((member) => !needle || member.name.toLowerCase().startsWith(needle));
  if (items.length === 0) return null;
  return (
    <div className="cmt-tags" role="listbox" aria-label={t('comments.mentionSomeone')}>
      {items.map((member) => (
        <button
          key={member.id}
          type="button"
          role="option"
          aria-selected={false}
          className="cmt-tags__item nb"
          onClick={() => onPick(draft.replace(/@[^\s@]*$/, `@${member.name} `))}
        >
          <span className="cmt-tags__at" aria-hidden="true">
            @
          </span>
          {member.name}
        </button>
      ))}
    </div>
  );
}

export interface ThreadActions {
  onReply: (threadId: string, text: string) => Promise<void>;
  onEdit: (commentId: string, text: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onSetResolved: (threadId: string, resolved: boolean) => Promise<void>;
  onToggle: (threadId: string | null) => void;
}

function Avatar({ name, size }: { name: string; size: 26 | 28 }): React.ReactElement {
  return (
    <span className={`cmt-avatar cmt-avatar--${size}`} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

function MessageHeader({ message }: { message: CommentMessage }): React.ReactElement {
  return (
    <div className="cmt-msg__head">
      <Avatar name={message.authorName} size={26} />
      <span className="cmt-msg__who">{message.authorName}</span>
      <span className="cmt-msg__time">
        {formatCommentTime(message.createdAt)}
        {message.edited ? ` · ${t('comments.edited')}` : ''}
      </span>
    </div>
  );
}

export function ThreadCard({
  thread,
  open,
  currentUserId,
  members,
  actions,
}: {
  thread: CommentThread;
  open: boolean;
  currentUserId: string;
  members: WorkspaceMember[];
  actions: ThreadActions;
}): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const first = thread.messages[0];
  const last = thread.messages[thread.messages.length - 1];
  const rootIsMine = first?.authorId === currentUserId;

  async function sendReply(): Promise<void> {
    const text = replyDraft.trim();
    if (!text) return;
    await actions.onReply(thread.id, text);
    setReplyDraft('');
  }

  async function saveEdit(commentId: string): Promise<void> {
    const text = editDraft.trim();
    if (!text) return;
    await actions.onEdit(commentId, text);
    setEditingId(null);
  }

  const tools = (
    <div className="cmt-tools">
      <button
        type="button"
        className="cmt-tools__btn nb"
        aria-label={thread.resolved ? t('comments.reopen') : t('comments.resolve')}
        title={thread.resolved ? t('comments.reopen') : t('comments.resolve')}
        onClick={(event) => {
          event.stopPropagation();
          void actions.onSetResolved(thread.id, !thread.resolved);
        }}
      >
        <Glyph d="m5 12 5 5 9-10" size={16} />
      </button>
      <ActionMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        label={t('comments.threadOptions')}
        className="menu--thread"
        trigger={
          <button
            type="button"
            className="cmt-tools__btn nb"
            aria-label={t('comments.moreOptions')}
            title={t('comments.moreOptions')}
          >
            <Glyph d={['M5 12h.01', 'M12 12h.01', 'M19 12h.01']} size={16} />
          </button>
        }
      >
        <ActionMenuItem
          onSelect={() =>
            void navigator.clipboard?.writeText(window.location.href).catch(() => undefined)
          }
        >
          {t('comments.copyLink')}
        </ActionMenuItem>
        {rootIsMine && first ? (
          <ActionMenuItem danger onSelect={() => void actions.onDelete(first.id)}>
            {t('comments.deleteThread')}
          </ActionMenuItem>
        ) : null}
      </ActionMenu>
    </div>
  );

  if (!open) {
    return (
      <div
        className={thread.resolved ? 'cmt-card is-resolved' : 'cmt-card'}
        role="button"
        tabIndex={0}
        aria-label={t('comments.openThread', { name: first?.authorName ?? '' })}
        aria-expanded={false}
        onClick={() => actions.onToggle(thread.id)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') actions.onToggle(thread.id);
        }}
      >
        {tools}
        {thread.resolved ? (
          <div className="cmt-card__resolved">{`✓ ${t('comments.resolved')}`}</div>
        ) : null}
        {first ? (
          <>
            <div className="cmt-card__head">
              <Avatar name={first.authorName} size={28} />
              <span className="cmt-card__who">{first.authorName}</span>
              <span className="cmt-msg__time">{formatCommentTime(first.createdAt)}</span>
            </div>
            <div className="cmt-card__body">
              <span className="cmt-card__line" aria-hidden="true">
                {thread.messages.length > 1 ? <span className="cmt-card__bar" /> : null}
              </span>
              <p className="cmt-card__text">{renderMessageText(first.text, members)}</p>
            </div>
          </>
        ) : null}
        {thread.messages.length > 2 ? (
          <div className="cmt-card__body">
            <span className="cmt-card__line" aria-hidden="true">
              <span className="cmt-card__bar" />
            </span>
            <p className="cmt-card__mid">
              {t(thread.messages.length - 2 === 1 ? 'comments.showReply' : 'comments.showReplies', {
                count: String(thread.messages.length - 2),
              })}
            </p>
          </div>
        ) : null}
        {last && thread.messages.length > 1 ? (
          <>
            <div className="cmt-card__head cmt-card__head--last">
              <Avatar name={last.authorName} size={28} />
              <span className="cmt-card__who">{last.authorName}</span>
              <span className="cmt-msg__time">{formatCommentTime(last.createdAt)}</span>
            </div>
            <p className="cmt-card__text cmt-card__text--last">
              {renderMessageText(last.text, members)}
            </p>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="cmt-card cmt-card--open" role="dialog" aria-label={t('comments.thread')}>
      {tools}
      {thread.messages.map((message) => (
        <div key={message.id} className="cmt-msg">
          <MessageHeader message={message} />
          {editingId === message.id ? (
            <div className="cmt-msg__edit">
              <textarea
                className="cmt-input cmt-input--area"
                value={editDraft}
                aria-label={t('comments.edit')}
                autoFocus
                rows={2}
                onChange={(event) => setEditDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void saveEdit(message.id);
                  }
                  if (event.key === 'Escape') setEditingId(null);
                }}
              />
              <div className="cmt-msg__editBtns">
                <button
                  type="button"
                  className="btn-prim cmt-btn--sm"
                  onClick={() => void saveEdit(message.id)}
                >
                  {t('comments.save')}
                </button>
                <button type="button" className="cmt-btn--sm nb" onClick={() => setEditingId(null)}>
                  {t('comments.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="cmt-msg__text">{renderMessageText(message.text, members)}</p>
              {message.authorId === currentUserId ? (
                <div className="cmt-msg__actions">
                  <button
                    type="button"
                    className="cmt-msg__action nb"
                    onClick={() => {
                      setEditingId(message.id);
                      setEditDraft(message.text);
                    }}
                  >
                    {t('comments.edit')}
                  </button>
                  <button
                    type="button"
                    className="cmt-msg__action nb is-danger"
                    onClick={() => void actions.onDelete(message.id)}
                  >
                    {t('comments.delete')}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ))}
      <MentionList draft={replyDraft} members={members} onPick={setReplyDraft} />
      <div className="cmt-reply">
        <input
          className="cmt-input"
          value={replyDraft}
          placeholder={t('comments.replyPlaceholder')}
          aria-label={t('comments.reply')}
          onChange={(event) => setReplyDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void sendReply();
            }
            if (event.key === 'Escape') actions.onToggle(null);
          }}
        />
        <button type="button" className="btn-prim cmt-reply__send" onClick={() => void sendReply()}>
          {t('comments.send')}
        </button>
      </div>
      <div>
        <button
          type="button"
          className="cmt-resolveBtn nb"
          onClick={() => void actions.onSetResolved(thread.id, !thread.resolved)}
        >
          {thread.resolved ? t('comments.reopen') : t('comments.resolve')}
        </button>
      </div>
    </div>
  );
}
